'use client'

import * as styles from './Search.css'
import {
  type CSSProperties,
  memo,
  useRef,
  useState,
  useEffect,
  useTransition,
} from 'react'
import { Search as SearchIcon } from 'lucide-react'
import { Row } from '@christiankaindl/lyts'
import Link from 'next/link'
import useKeyPress from '@shared/hooks/useKeyPress'
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip/Tooltip'
import {
  Combobox,
  ComboboxProvider,
  useComboboxStore,
  type DialogStore,
} from '@ariakit/react'
import { useRouter, useSearchParams } from 'next/navigation'
import Button from '../Button/Button'
import dynamic from 'next/dynamic'
import useFirstRender from '@shared/hooks/useFirstRender'
import Text from '@shared/components/Text/Text'
import { create } from 'zustand'

const SearchPopover = dynamic(async () => await import('./SearchPopover'))

interface SearchOverlayStore {
  dialog: DialogStore | null
}

export const useSearchOverlay = create<SearchOverlayStore>()(() => ({
  dialog: null,
}))

interface SearchProps {
  center?: boolean
  style?: CSSProperties
  placeholder?: string
  onSelect?: () => void
  id?: string
  autoFocus?: boolean
  enableShortcut?: boolean
  inputProps?: React.HTMLProps<HTMLInputElement>
  className?: string
  slackMsgEnvironmentPrefix: string
}

let modifierKeyPrefix = 'Strg' // control key
if (typeof navigator !== 'undefined') {
  if (
    navigator?.platform?.indexOf('Mac') === 0 ||
    navigator?.platform === 'iPhone'
  ) {
    modifierKeyPrefix = '⌘' // command key
  }
}

const Search = memo(function Search({
  style = {},
  inputProps,
  placeholder,
  className,
  id,
  autoFocus = false,
  enableShortcut = true,
  slackMsgEnvironmentPrefix,
}: SearchProps) {
  const router = useRouter()
  const params = useSearchParams()
  const q = params?.get('q')

  const [isPending, startTransition] = useTransition()

  const combobox = useComboboxStore({
    placement: 'bottom',
    defaultValue: q ?? '',
  })
  const value = combobox.useState('value')
  const activeId = combobox.useState('activeId')
  const [isFocused, setIsFocused] = useState(autoFocus)
  const ref = useRef<HTMLInputElement>(null)

  const isHome = id === 'home-search'
  if (isHome && typeof window !== 'undefined') {
    // @ts-expect-error Used to trigger the search on the homepage
    window._workaroundOpenSearch = () => {
      ref?.current?.focus()
      combobox.setOpen(true)
    }
  }

  function focusSearchInput(event: KeyboardEvent | PointerEvent) {
    event.preventDefault()

    ref?.current?.focus()
    combobox.setOpen(true)
  }

  useKeyPress({
    targetKey: 'k',
    onKeyDown(event) {
      if (!event.metaKey && !event.ctrlKey) return

      focusSearchInput(event)
    },
    disabled: !enableShortcut,
  })

  function submitSearch() {
    if (!value) return

    startTransition(() => {
      router.push(`/suche?q=${encodeURIComponent(value)}`)
      combobox.setOpen(false)
    })
  }

  useEffect(
    function syncUrlToInput() {
      if (isHome) return

      combobox.setValue(q ?? '')
      combobox.setOpen(false)
    },
    [combobox, isHome, q],
  )

  return (
    <ComboboxProvider store={combobox}>
      <form
        action={submitSearch}
        style={{ position: 'relative', ...style }}
        className={className}
      >
        <Combobox
          render={
            <Input
              isPending={isPending}
              rootProps={{ id: isHome ? 'home-search-root' : undefined }}
              inputProps={inputProps}
            />
          }
          onFocus={() => {
            setIsFocused(true)
          }}
          onBlur={() => {
            setIsFocused(false)
          }}
          // Capture phase: Ariakit clicks the active item here and clears activeId before bubbling,
          // and it swallows Enter while the popover is open so the form never submits on its own.
          onKeyDownCapture={(event) => {
            if (
              event.key !== 'Enter' ||
              event.nativeEvent.isComposing ||
              combobox.getState().activeId
            )
              return
            event.preventDefault()
            submitSearch()
          }}
          id={id}
          name='query'
          autoFocus={autoFocus}
          ref={ref}
          placeholder={placeholder}
        />
        {isHome && (
          <Button
            onPointerDown={submitSearch}
            size='small'
            style={{
              position: 'absolute',
              right: 6,
              top: 6,
              height: 33,
              padding: '0 12px',
              backgroundColor: 'rgb(234, 212, 176, 0.44)',
              color: '#956A25',
            }}
          >
            Suchen
          </Button>
        )}
        {!isHome && isFocused && ((value && value.length < 25) || activeId) && (
          <Button
            size='tiny'
            onPointerDown={submitSearch}
            className={styles.keyboardHint}
          >
            Suchen
          </Button>
        )}
        {!isHome && !isFocused && (
          <KeyboardHint focusInput={focusSearchInput} />
        )}
      </form>
      <SearchPopover
        query={value}
        slackMsgEnvironmentPrefix={slackMsgEnvironmentPrefix}
      />
    </ComboboxProvider>
  )
})

Search.displayName = 'Search'
export default Search

function KeyboardHint({
  focusInput,
  ...props
}: {
  focusInput: (event: KeyboardEvent | PointerEvent) => void
}) {
  const isFirstRender = useFirstRender()

  return (
    <Tooltip>
      <div className='fade-in'>
        <TooltipTrigger
          render={
            <Button
              size='tiny'
              onPointerDown={(event) => {
                focusInput(event.nativeEvent)
              }}
              className={styles.keyboardHint}
              {...props}
            />
          }
        >
          {!isFirstRender && modifierKeyPrefix}+K
        </TooltipTrigger>
      </div>
      <TooltipContent>Suche fokussieren</TooltipContent>
    </Tooltip>
  )
}

type InputProps = React.HTMLProps<HTMLInputElement> & {
  rootProps?: React.HTMLProps<HTMLDivElement>
  inputProps?: React.HTMLProps<HTMLInputElement>
  icon?: boolean
  endWidget?: React.ReactNode
  isPending?: boolean
}
export function Input({
  style,
  placeholder = 'Recht durchsuchen...',
  icon = true,
  rootProps = {},
  inputProps = {},
  endWidget,
  isPending = false,
  ref,
  ..._inputProps
}: InputProps) {
  return (
    <div
      {...rootProps}
      className={`${styles.root} ${rootProps?.className ?? ''}`}
      aria-busy={isPending}
      style={style}
      aria-disabled={inputProps.disabled}
    >
      {icon && <SearchIcon size={20} className={styles.icon} />}
      <input
        {..._inputProps}
        {...inputProps}
        ref={ref}
        className={`${styles.input} ${inputProps.className ?? ''}`}
        placeholder={placeholder}
        style={{ paddingLeft: icon ? 47 : 15 }}
      />
      {endWidget}
    </div>
  )
}

type SearchHit = {
  q?: string
  _highlightResult?: { q?: { matchLevel?: string } }
}
interface ResultItemProps {
  hit: SearchHit
  query?: string
  insights?: () => void
  style?: CSSProperties
  onSelect?: () => void
}
export function ResultItem({
  hit,
  style = {},
  onSelect = () => {},
  query,
  ...props
}: ResultItemProps) {
  if (!hit.q) {
    return null
  }

  return (
    <Row gap='12px' asChild style={style} {...props}>
      <Link
        href={`/suche?q=${encodeURIComponent(query ?? (hit.q as string))}`}
        className={`${styles.resultItem} ${hit._highlightResult?.q?.matchLevel !== 'none' ? styles.hasInput : ''}`}
        onClick={() => {
          onSelect?.()
        }}
      >
        <SearchIcon size={16} style={{ opacity: 0.35 }} />
        {hit.q && <Text>{hit.q}</Text>}
      </Link>
    </Row>
  )
}
